/**
 * Widget Utilities
 * Shared helper functions for widget components
 */

/**
 * Safely convert a value to a displayable string.
 * Prevents React "Objects are not valid as a React child" crash.
 * 
 * @param {*} value - Any value (may be object, array, null, etc.)
 * @param {string} defaultValue - Fallback if value is not renderable
 * @returns {string|number} Safe renderable value
 */
export const safeDisplayValue = (value, defaultValue = '-') => {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === 'object') return defaultValue;
  return value;
};

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Data object
 * @param {string} path - Dot-notated path (e.g., "risk_stats.high")
 * @param {*} defaultValue - Default value if path not found
 * @returns {*} Value at path or default
 */
export const getNestedValue = (obj, path, defaultValue = '-') => {
  if (!obj || !path) return defaultValue;
  
  const value = path.split('.').reduce((acc, key) => {
    return acc && acc[key] !== undefined ? acc[key] : undefined;
  }, obj);
  
  return value !== undefined ? value : defaultValue;
};

/**
 * Format string with data values
 * Replaces {fieldName} patterns with actual values from data
 * @param {string} format - Format string (e.g., "{manufacturer} / {model}")
 * @param {Object} data - Data object
 * @returns {string} Formatted string
 */
export const formatString = (format, data) => {
  if (!format || !data) return '-';
  
  return format.replace(/\{([^}]+)\}/g, (match, key) => {
    const value = getNestedValue(data, key.trim(), '-');
    return value;
  });
};

/**
 * Calculate D-day from date string
 * @param {string} dateStr - Date string (YYYY-MM-DD format)
 * @returns {number} Days until date (negative if past)
 */
export const calculateDday = (dateStr) => {
  if (!dateStr) return null;
  
  const targetDate = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);
  
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Get color classes for a given color name
 * @param {string} color - Color name (red, orange, emerald, indigo, slate)
 * @returns {Object} Color class mappings
 */
export const getColorClasses = (color) => {
  const colorMap = {
    red: {
      text: 'text-red-400',
      bg: 'bg-red-500',
      bgLight: 'bg-red-500/5',
      border: 'rgba(239,68,68,0.1)',
      dot: 'bg-red-500'
    },
    orange: {
      text: 'text-orange-400',
      bg: 'bg-orange-500',
      bgLight: 'bg-orange-500/5',
      border: 'rgba(245,158,11,0.1)',
      dot: 'bg-orange-500'
    },
    emerald: {
      text: 'text-emerald-400',
      bg: 'bg-emerald-500',
      bgLight: 'bg-emerald-500/5',
      border: 'rgba(16,185,129,0.1)',
      dot: 'bg-emerald-500'
    },
    indigo: {
      text: 'text-indigo-400',
      bg: 'bg-indigo-500',
      bgLight: 'bg-indigo-500/5',
      border: 'rgba(99,102,241,0.1)',
      dot: 'bg-indigo-500'
    },
    slate: {
      text: 'text-slate-400',
      bg: 'bg-slate-500',
      bgLight: 'bg-slate-500/5',
      border: 'rgba(148,163,184,0.1)',
      dot: 'bg-slate-500'
    },
    blue: {
      text: 'text-blue-400',
      bg: 'bg-blue-500',
      bgLight: 'bg-blue-500/5',
      border: 'rgba(59,130,246,0.1)',
      dot: 'bg-blue-500'
    },
    purple: {
      text: 'text-purple-400',
      bg: 'bg-purple-500',
      bgLight: 'bg-purple-500/5',
      border: 'rgba(168,85,247,0.1)',
      dot: 'bg-purple-500'
    },
    pink: {
      text: 'text-pink-400',
      bg: 'bg-pink-500',
      bgLight: 'bg-pink-500/5',
      border: 'rgba(236,72,153,0.1)',
      dot: 'bg-pink-500'
    },
    cyan: {
      text: 'text-cyan-400',
      bg: 'bg-cyan-500',
      bgLight: 'bg-cyan-500/5',
      border: 'rgba(6,182,212,0.1)',
      dot: 'bg-cyan-500'
    },
    yellow: {
      text: 'text-yellow-400',
      bg: 'bg-yellow-500',
      bgLight: 'bg-yellow-500/5',
      border: 'rgba(234,179,8,0.1)',
      dot: 'bg-yellow-500'
    }
  };

  return colorMap[color] || colorMap.slate;
};

/**
 * Resolve conditional color based on data value and colorRules
 * Rules are evaluated top-to-bottom, first match wins.
 * If no rule matches, returns the defaultColor.
 * 
 * @param {number|string} dataValue - The actual data value to evaluate
 * @param {string} defaultColor - Fallback color name (e.g., 'slate')
 * @param {Array} colorRules - Array of { op, value, color } objects
 *   op: '>', '>=', '<', '<=', '==', '!='
 *   value: number to compare against
 *   color: color name to apply if rule matches
 * @returns {string} Resolved color name
 */
export const resolveConditionalColor = (dataValue, defaultColor, colorRules) => {
  if (!colorRules || !Array.isArray(colorRules) || colorRules.length === 0) {
    return defaultColor || 'slate';
  }

  const numValue = Number(dataValue);
  if (isNaN(numValue)) return defaultColor || 'slate';

  for (const rule of colorRules) {
    if (!rule.op || rule.value === undefined || rule.value === '') continue;

    const ruleValue = Number(rule.value);
    if (isNaN(ruleValue)) continue;

    let matched = false;
    switch (rule.op) {
      case '>':  matched = numValue > ruleValue; break;
      case '>=': matched = numValue >= ruleValue; break;
      case '<':  matched = numValue < ruleValue; break;
      case '<=': matched = numValue <= ruleValue; break;
      case '==': matched = numValue === ruleValue; break;
      case '!=': matched = numValue !== ruleValue; break;
      default: break;
    }

    if (matched) return rule.color || defaultColor || 'slate';
  }

  return defaultColor || 'slate';
};
